<template>
  <div
    :class="['c-custom-audio-player', `c-custom-audio-player--${type}`]"
    aria-label="Audio Player"
    role="region"
  >
    <div v-if="type === 'full'" class="c-custom-audio-player__wrapper c-custom-audio-player__wrapper--top">
      <button
        :class="['c-custom-audio-player__button', 'c-custom-audio-player__button--skip']"
        :title="translate(SKIP_TIME_BACKWARD, {time: this.skipBackwardTime})"
        :aria-label="translate(SKIP_TIME_BACKWARD, {time: this.skipBackwardTime})"
        :data-skip-time="skipBackwardTime"
        @click="skipBackward"
      >
        <i class="icon-backward" />
      </button>
      <button
        :class="['c-custom-audio-player__button', `c-custom-audio-player__button--${!isPlay ? 'play' : 'pause'}`]"
        :title="!isPlay ? translate(PLAY) : translate(PAUSE)"
        :aria-label="!isPlay ? translate(PLAY) : translate(PAUSE)"
        @click="togglePlay"
      >
        <i :class="`icon-${!isPlay ? 'play' : 'pause'}`" />
      </button>
      <button
        :class="['c-custom-audio-player__button', 'c-custom-audio-player__button--skip']"
        :title="translate(SKIP_TIME_FORWARD, {time: this.skipForwardTime})"
        :aria-label="translate(SKIP_TIME_FORWARD, {time: this.skipForwardTime})"
        :data-skip-time="skipForwardTime"
        @click="skipForward"
      >
        <i class="icon-forward" />
      </button>

      <button
        :class="['c-custom-audio-player__button', 'c-custom-audio-player__button--sound']"
        :title="muted ? translate(SOUND_TURN_ON) : translate(SOUND_TURN_OFF)"
        :aria-label="muted ? translate(SOUND_TURN_ON) : translate(SOUND_TURN_OFF)"
        @click="toggleMuted"
      >
        <i :class="`icon-${muted ? 'no-sound' : 'full-sound'}`" />
        <div class="c-custom-audio-play__sound-slider">
          <VueSlider
            ref="sound-slider"
            :value="muted ? 0 : volume"
            v-bind="soundSliderOptions"
            :tooltip-formatter="tooltipFormatter"
            @change="updateVolume"
          />
        </div>
      </button>
    </div>
    <div class="c-custom-audio-player__wrapper c-custom-audio-player__wrapper--bottom">
      <div v-if="type === 'simple'" class="c-custom-audio-player__simple c-custom-audio-player__simple--left">
        <button
          :class="['c-custom-audio-player__button', `c-custom-audio-player__button--${!isPlay ? 'play' : 'pause'}`]"
          :title="!isPlay ? translate(PLAY) : translate(PAUSE)"
          :aria-label="!isPlay ? translate(PLAY) : translate(PAUSE)"
          @click="togglePlay"
        >
          <i :class="`icon-${!isPlay ? 'play' : 'pause'}`" />
        </button>
        <button
          :class="['c-custom-audio-player__button', 'c-custom-audio-player__button--skip']"
          :title="translate(SKIP_TIME_BACKWARD, {time: this.skipBackwardTime})"
          :aria-label="translate(SKIP_TIME_BACKWARD, {time: this.skipBackwardTime})"
          :data-skip-time="skipBackwardTime"
          @click="skipBackward"
        >
          <i class="icon-backward" />
        </button>
        <button
          :class="['c-custom-audio-player__button', 'c-custom-audio-player__button--skip']"
          :title="translate(SKIP_TIME_FORWARD, {time: this.skipForwardTime})"
          :aria-label="translate(SKIP_TIME_FORWARD, {time: this.skipForwardTime})"
          :data-skip-time="skipForwardTime"
          @click="skipForward"
        >
          <i class="icon-forward" />
        </button>
      </div>
      <div class="c-custom-audio-player__time c-custom-audio-player__time--current-time">
        {{ formatCurrentTime }}
      </div>
      <VueSlider
        ref="slider"
        class="c-custom-audio-player__slider"
        tabindex="0"
        :value="floorCurrentTime"
        :max="floorDuration"
        :tooltip-formatter="tooltipFormatter"
        v-bind="sliderOptions"
        @keyup.left.native="timeBackwardArrow"
        @keyup.right.native="timeForwardArrow"
        @change="updateCurrentTime"
      />
      <div class="c-custom-audio-player__time c-custom-audio-player__time--full-time">
        {{ formatFullTime }}
      </div>
      <div v-if="type === 'simple'" class="c-custom-audio-player__simple c-custom-audio-player__simple--right">
        <button
          :class="['c-custom-audio-player__button', 'c-custom-audio-player__button--sound']"
          :title="muted ? translate(SOUND_TURN_ON) : translate(SOUND_TURN_OFF)"
          :aria-label="muted ? translate(SOUND_TURN_ON) : translate(SOUND_TURN_OFF)"
          @click="toggleMuted"
        >
          <i :class="`icon-${muted ? 'no-sound' : 'full-sound'}`" />
          <div class="c-custom-audio-play__sound-slider">
            <VueSlider
              ref="sound-slider"
              :value="muted ? 0 : volume"
              v-bind="soundSliderOptions"
              :tooltip-formatter="tooltipFormatter"
              @change="updateVolume"
            />
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component'

import { formatTime } from './utils'

export default {
  name: 'CustomAudioPlayerFull',

  components: {
    VueSlider
  },

  props: {
    type: {
      type: String,
      default: 'full'
    },
    isPlay: Boolean,
    muted: Boolean,
    volume: Number,
    currentTime: Number,
    duration: Number,
    skipForwardTime: {
      type: Number,
      default: 15
    },
    skipBackwardTime: {
      type: Number,
      default: 15
    },
    SKIP_TIME_BACKWARD: {
      type: String,
      default: 'Skip time {time}s backward'
    },
    SKIP_TIME_FORWARD: {
      type: String,
      default: 'Skip time {time}s forward'
    },
    SOUND_TURN_OFF: {
      type: String,
      default: 'Turn off sound'
    },
    SOUND_TURN_ON: {
      type: String,
      default: 'Turn on sound'
    },
    PLAY: {
      type: String,
      default: 'Play'
    },
    PAUSE: {
      type: String,
      default: 'Pause'
    }
  },

  data() {
    return {
      sliderOptions: {
        dotSize: 10,
        height: 4,
        width: '100%',
        interval: 1,
        lazy: true,
        duration: 0,
        tooltip: 'hover',
        tooltipPlacement: 'top',
        dragOnClick: true
      },
      soundSliderValue: 1,
      soundSliderOptions: {
        dotSize: 8,
        height: '100%',
        max: 1,
        min: 0,
        width: 4,
        direction: 'btt',
        interval: 0.1,
        duration: 0,
        tooltip: 'none',
        dragOnClick: true
      }
    }
  },

  computed: {
    floorCurrentTime() {
      return Math.floor(this.currentTime)
    },

    formatCurrentTime() {
      return formatTime(this.floorCurrentTime)
    },

    floorDuration() {
      return Math.floor(this.duration)
    },

    formatFullTime() {
      return formatTime(this.floorDuration)
    }
  },

  methods: {
    tooltipFormatter(value) {
      return formatTime(Math.ceil(value))
    },

    togglePlay() {
      this.$emit('toggle-play')
    },

    toggleMuted(ev) {
      if (
        ev.target.className.indexOf('c-custom-audio-player__button--sound') !== -1 ||
        ev.target.className.indexOf('icon-') !== -1
      ) {
        this.$emit('toggle-muted')
      }
    },

    timeForwardArrow() {
      this.$emit('update-current-time', this.currentTime + 1)
    },

    timeBackwardArrow() {
      this.$emit('update-current-time', this.currentTime - 1)
    },

    updateVolume(value) {
      this.$emit('update-volume', value)
    },

    updateCurrentTime(time) {
      this.$emit('update-current-time', time)
    },

    skipBackward() {
      this.$emit('update-current-time', this.currentTime - this.skipBackwardTime)
    },

    skipForward() {
      this.$emit('update-current-time', this.currentTime + this.skipForwardTime)
    },

    translate(sentence, props = {}) {
      const splitByFormat = sentence.split(/{(.*)}/g)
      const isFormatable = splitByFormat.length > 1

      if (isFormatable && Object.keys(props).length) {
        Object.keys(props).forEach(key => {
          const index = splitByFormat.indexOf(key)

          if (index !== -1) {
            splitByFormat[index] = props[key]
          }
        })

        return splitByFormat.join('')
      }

      return sentence
    }
  }
}
</script>

<style lang="scss">
@import 'font.css';
$themeColor: #7D91F9;
$dotBgColor: #7D91F9;
@import '~vue-slider-component/lib/theme/default.scss';

.c-custom-audio-player {
  background: #474747;
  padding: 1em;
  max-width: 40em;
}

.c-custom-audio-player__simple {
  display: flex;

  &--left {
    margin-right: 0.75em;
  }

  &--right {
    margin-left: 0.75em;
  }
}

.c-custom-audio-player__button {
  background: transparent;
  border: 0;
  margin: 0;
  padding: 0;
  border: 0;
  cursor: pointer;
  appearance: none;
  font-size: 1.3em;

  margin: 0 0.4em;

  &--play,
  &--pause {
    .c-custom-audio-player__wrapper--top & {
      font-size: 1.4em;
    }
  }

  &--skip {
    position: relative;

    &::after {
      content: attr(data-skip-time);
      color: #fff;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 0;
      right: 0;
      text-align: center;
      font-size: 0.5em;
    }
  }

  &--sound {
    position: relative;

    .c-custom-audio-player__wrapper--top & {
      position: absolute;
      font-size: 1em;
      top: 50%;
      transform: translateY(-50%);
      right: 0;
      padding: 0.5em 0;
    }
  }

  &:hover {
    [class^="icon-"], [class*=" icon-"] {
      &:before {
        color: $themeColor;
      }
    }
  }
}

.c-custom-audio-player__wrapper {
  display: flex;
  align-items: center;

  &--top {
    position: relative;
    margin-bottom: 0.5em;
    justify-content: center;
  }
}

.c-custom-audio-player__time {
  color: #fff;
  font-size: 0.9em;

  &--current-time {
    margin-right: 1em;
  }

  &--full-time {
    margin-left: 1em;
  }
}

.c-custom-audio-play__sound-slider {
  display: none;
  position: absolute;
  right: 0.1em;
  top: -4.1em;
  height: 3em;
  background: #6F6F6F;
  padding: 0.6em 0.2em;

  .c-custom-audio-player__simple & {
    top: -4.2em;
  }

  .c-custom-audio-player__button--sound:hover & {
    display: block;
  }
}
</style>
